/**
 * Client-side identity.
 *
 * One source of truth (`/api/me`), one cache, and a BroadcastChannel so every
 * open tab agrees. Without the channel, signing in on one tab leaves the others
 * believing they are anonymous, and their next save 401s for no visible reason.
 */
import { remainingProjectQuota as quotaFor, type Tier } from "../../shared/tiers";
import { promoteIfSignedIn } from "./device-memory";

export type { Tier };

export interface SubscriptionState {
  tier: Tier;
  status: string;
  /** Unix ms; when the paid period ends. */
  currentPeriodEnd: number | null;
  /** Cancelled, but still inside the period already paid for. */
  cancelAtPeriodEnd: boolean;
  /** A payment failed and Stripe is retrying. Access is not revoked. */
  pastDue: boolean;
}

export const NO_SUBSCRIPTION: SubscriptionState = {
  tier: "free",
  status: "none",
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  pastDue: false,
};

export interface Identity {
  signedIn: boolean;
  user: { id: string; name: string; email: string; image: string | null } | null;
  devUser: string | null;
  tier: Tier;
  /** Billing state, so the UI can warn before the user finds out the hard way. */
  subscription: SubscriptionState;
  /**
   * Paid tiers this site can actually sell right now.
   *
   * Rendered from rather than hardcoded, so a tier whose Stripe prices are not
   * wired never appears with a button that would 400. It is also how Studio
   * stays pinned — see docs/decisions/pricing-and-checkout.md.
   */
  sellableTiers: Exclude<Tier, "free">[];
  authConfigured: boolean;
  providers: { discord: boolean; google: boolean };
}

export const SIGNED_OUT: Identity = {
  signedIn: false,
  user: null,
  devUser: null,
  tier: "free",
  subscription: NO_SUBSCRIPTION,
  sellableTiers: [],
  authConfigured: false,
  providers: { discord: false, google: false },
};

/** Exhaustive at compile time; asserted against the live /api/me response. */
export const IDENTITY_KEYS = Object.keys({
  signedIn: true,
  user: true,
  devUser: true,
  tier: true,
  subscription: true,
  sellableTiers: true,
  authConfigured: true,
  providers: true,
} satisfies Record<keyof Identity, true>);

export const SUBSCRIPTION_STATE_KEYS = Object.keys({
  tier: true,
  status: true,
  currentPeriodEnd: true,
  cancelAtPeriodEnd: true,
  pastDue: true,
} satisfies Record<keyof SubscriptionState, true>);

type Listener = (identity: Identity) => void;

const listeners = new Set<Listener>();
let current: Identity = SIGNED_OUT;
let inflight: Promise<Identity> | null = null;

const channel: BroadcastChannel | null =
  typeof BroadcastChannel === "function" ? new BroadcastChannel("rcm-identity") : null;

// A sibling tab changed identity. Refetch rather than trusting the payload —
// the cookie is the authority, not another tab's opinion of it.
channel?.addEventListener("message", (e) => {
  if ((e as MessageEvent).data === "changed") void refresh();
});

function emit(next: Identity) {
  current = next;
  for (const l of listeners) l(next);
}

/** Last known identity. Synchronous; call `refresh()` to update it. */
export function identity(): Identity {
  return current;
}

export function onIdentityChange(listener: Listener): () => void {
  listeners.add(listener);
  listener(current);
  return () => listeners.delete(listener);
}

/** Fetch identity. Concurrent calls share one request. */
export function refresh(): Promise<Identity> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch("/api/me", { credentials: "same-origin" });
      if (!res.ok) throw new Error(`/api/me returned ${res.status}`);
      const next = (await res.json()) as Identity;
      // A confirmed session is the only trustworthy evidence that a sign-in
      // round trip succeeded, so this is where a pending provider becomes the
      // remembered one.
      promoteIfSignedIn(next.signedIn);
      emit(next);
      return next;
    } catch {
      // Being offline is not being signed out. Keep the last known identity
      // rather than flickering the UI to anonymous on a dropped connection.
      return current;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Tell other tabs to re-read. Call after any identity-changing action. */
export function announceChange() {
  channel?.postMessage("changed");
}

/**
 * Handle a 401 from any API call.
 *
 * The session ended elsewhere — expired, signed out in another tab, or revoked.
 * Drop to signed-out and let the UI re-render, rather than retrying into a loop
 * against a session that is not coming back.
 */
export function handleUnauthorized() {
  if (current.signedIn) {
    emit({ ...SIGNED_OUT, authConfigured: current.authConfigured, providers: current.providers });
    announceChange();
  }
}

/** fetch() that keeps identity honest when the session dies mid-session. */
export async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, { ...init, credentials: "same-origin" });
  if (res.status === 401) handleUnauthorized();
  return res;
}

/**
 * Re-exported from shared/tiers so the shell keeps its existing import.
 *
 * The limits themselves used to be a second copy of the numbers in
 * `worker/storage.ts` — the day the free quota moved, one of them would have
 * said 3 while the other said 5, and the UI would have been quietly lying about
 * what the server would accept.
 */
export const remainingProjectQuota = quotaFor;
